import React from "react"
import PropTypes from "prop-types"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import { Link } from "react-router-dom"
import { CSVLink } from "react-csv"
import {
  IconButton,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
} from "@material-ui/core"
import FirstPageIcon from "@material-ui/icons/FirstPage"
import KeyboardArrowLeft from "@material-ui/icons/KeyboardArrowLeft"
import KeyboardArrowRight from "@material-ui/icons/KeyboardArrowRight"
import LastPageIcon from "@material-ui/icons/LastPage"
import GetAppIcon from "@material-ui/icons/GetApp"
import ArrowBackIcon from "@material-ui/icons/ArrowBack"

import api from "../../services/api"

import "./Reports.scss"

const useStyles1 = makeStyles((theme) => ({
  root: {
    flexShrink: 0,
    marginLeft: theme.spacing(2.5),
  },
}))

function TablePaginationActions(props) {
  const classes = useStyles1()
  const theme = useTheme()
  const { count, page, rowsPerPage, onChangePage } = props

  const handleFirstPageButtonClick = (event) => onChangePage(event, 0)
  const handleBackButtonClick = (event) => onChangePage(event, page - 1)
  const handleNextButtonClick = (event) => onChangePage(event, page + 1)
  const handleLastPageButtonClick = (event) =>
    onChangePage(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1))

  return (
    <div className={classes.root}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>

      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>

      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>

      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </div>
  )
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onChangePage: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
}

class Reports extends React.Component {
  constructor() {
    super()

    this.state = {
      report: [],
      reportFiltered: [],
      page: 0,
      rowsPerPage: 10,
      name: "",
      email: "",
      phone: "",
      course: "",
      dateScheduled: "",
      type: "",
      datePurchased: "",
      voucher: "",
      quantity: "",
    }
  }

  async componentDidMount() {
    const userId = localStorage.getItem("userId")

    if (!userId) {
      this.props.history.push("/login")
      return
    }

    try {
      const response = await api.get(`/reports/`)

      this.setState({
        report: response.data,
        reportFiltered: response.data,
      })
    } catch (e) {
      this.setState({
        report: [],
        reportFiltered: [],
      })
    }
  }

  cleanString(value) {
    return String(value || "")
      .trim()
      .replace(/\/+$/g, "")
  }

  formatDate(date) {
    if (!date) return ""

    const formDate = new Date(date)
    const day = String(formDate.getUTCDate()).padStart(2, "0")
    const month = String(formDate.getUTCMonth() + 1).padStart(2, "0")
    const year = formDate.getUTCFullYear()

    return `${day}/${month}/${year}`
  }

  formatHours(date) {
    if (!date) return ""

    const formDate = new Date(date)
    const hours = String(formDate.getUTCHours()).padStart(2, "0")
    const minutes = String(formDate.getUTCMinutes()).padStart(2, "0")

    return `${hours}:${minutes}`
  }

  formatDateKey(dateKey) {
    const cleanDateKey = this.cleanString(dateKey)

    if (!cleanDateKey) return ""

    const [year, month, day] = cleanDateKey.split("-")

    if (!year || !month || !day) return ""

    return `${day}/${month}/${year}`
  }

  getScheduledDate(row) {
    const selectedDateKey = this.cleanString(
      row?.selected_date_key || row?.selectedDateKey || "",
    )

    if (selectedDateKey) {
      return this.formatDateKey(selectedDateKey)
    }

    return this.formatDate(row?.date_course)
  }

  getScheduledHour(row) {
    const selectedHourKey = this.cleanString(
      row?.selected_hour_key || row?.selectedHourKey || "",
    )

    if (selectedHourKey) {
      return selectedHourKey
    }

    return this.formatHours(row?.date_course)
  }

  getScheduledDateTime(row) {
    const date = this.getScheduledDate(row)
    const hour = this.getScheduledHour(row)

    if (!date && !hour) return ""
    if (!hour) return date

    return `${date} - ${hour}`
  }

  getQty(row) {
    const q = Number(row?.quantity)
    return Number.isFinite(q) && q > 0 ? q : 1
  }

  getUnitPrice(row) {
    const p = Number(row?.unit_price ?? row?.course?.price)
    return Number.isFinite(p) ? p : 0
  }

  isCombo(row) {
    const name = String(row?.course?.name || "").toLowerCase()

    return (
      !!row?.course?.is_combo ||
      !!row?.course?.isCombo ||
      name.includes("combo")
    )
  }

  getOrderKey(row) {
    const orderId = this.cleanString(row?.order_id)
    const stripeSessionId = this.cleanString(row?.stripe_session_id)

    return orderId || stripeSessionId || String(row?._id || "")
  }

  groupRowsByOrder(rows) {
    const map = new Map()

    ;(Array.isArray(rows) ? rows : []).forEach((row) => {
      const key = this.getOrderKey(row)

      if (!map.has(key)) {
        map.set(key, {
          orderId: key,
          user: row.user || null,
          date_purchase: row.date_purchase,
          voucher: row.voucher || "",
          rows: [],
        })
      }

      map.get(key).rows.push(row)
    })

    return Array.from(map.values())
      .map((order) => {
        const comboRow = order.rows.find((row) => this.isCombo(row))
        const mainRow = comboRow || order.rows[0]

        const totalPaid = comboRow
          ? this.getUnitPrice(comboRow) * this.getQty(comboRow)
          : order.rows.reduce((acc, row) => {
              return acc + this.getUnitPrice(row) * this.getQty(row)
            }, 0)

        const scheduledItems = order.rows
          .map((row) => this.getScheduledDateTime(row))
          .filter(Boolean)

        const uniqueScheduledItems = [...new Set(scheduledItems)]

        return {
          ...order,
          mainRow,
          comboRow,
          isComboOrder: !!comboRow,
          totalPaid,
          scheduledDateTime: uniqueScheduledItems.join(" / "),
        }
      })
      .sort((a, b) => {
        return new Date(b.date_purchase) - new Date(a.date_purchase)
      })
  }

  getProductSold(order) {
    if (!order?.mainRow) return ""

    return order.mainRow?.course?.name || ""
  }

  handleDataCSV(orderRows) {
    const csv = []

    csv.push([
      "Name",
      "Email",
      "Phone",
      "Product Sold",
      "Date Scheduled",
      "Total Paid",
      "Date Purchased",
      "Voucher",
      "Order ID",
    ])

    orderRows.forEach((order) => {
      csv.push([
        order?.user?.name || "",
        order?.user?.email || "",
        order?.user?.phone || "",
        this.getProductSold(order),
        order.scheduledDateTime || "",
        order.totalPaid || 0,
        this.formatDate(order.date_purchase),
        order.voucher || "",
        order.orderId || "",
      ])
    })

    return csv
  }

  handleFilterChange(e) {
    const { id, value } = e.target

    this.setState(
      {
        [id]: value,
      },
      () => this.applyFilters(),
    )
  }

  applyFilters() {
    const {
      report,
      name,
      email,
      phone,
      course,
      dateScheduled,
      type,
      datePurchased,
      voucher,
      quantity,
    } = this.state

    const orderGroups = this.groupRowsByOrder(report)

    const filteredGroups = orderGroups.filter((order) => {
      const productSold = this.getProductSold(order)
      const coursesText = productSold.toLowerCase()

      const filters = {
        name: String(order?.user?.name || "").toLowerCase(),
        email: String(order?.user?.email || "").toLowerCase(),
        phone: String(order?.user?.phone || "").toLowerCase(),
        course: coursesText,
        dateScheduled: String(order?.scheduledDateTime || "").toLowerCase(),
        type: order.rows
          .map((row) => row?.course?.type || "")
          .join(" ")
          .toLowerCase(),
        datePurchased: this.formatDate(order.date_purchase).toLowerCase(),
        voucher: String(order?.voucher || "").toLowerCase(),
        quantity: String(
          order.rows.reduce((acc, row) => acc + this.getQty(row), 0),
        ),
      }

      return (
        filters.name.includes(name.toLowerCase().trim()) &&
        filters.email.includes(email.toLowerCase().trim()) &&
        filters.phone.includes(phone.toLowerCase().trim()) &&
        filters.course.includes(course.toLowerCase().trim()) &&
        filters.dateScheduled.includes(dateScheduled.toLowerCase().trim()) &&
        filters.type.includes(type.toLowerCase().trim()) &&
        filters.datePurchased.includes(datePurchased.toLowerCase().trim()) &&
        filters.voucher.includes(voucher.toLowerCase().trim()) &&
        filters.quantity.includes(quantity.toLowerCase().trim())
      )
    })

    const filteredRows = filteredGroups.flatMap((order) => order.rows)

    this.setState({
      reportFiltered: filteredRows,
      page: 0,
    })
  }

  render() {
    const {
      reportFiltered,
      page,
      rowsPerPage,
      name,
      email,
      phone,
      course,
      dateScheduled,
      type,
      datePurchased,
      voucher,
      quantity,
    } = this.state

    const rows =
      reportFiltered && reportFiltered.length > 0 ? reportFiltered : []

    const groupedOrders = this.groupRowsByOrder(rows)

    const emptyRows =
      rowsPerPage -
      Math.min(rowsPerPage, groupedOrders.length - page * rowsPerPage)

    const orderRows =
      rowsPerPage > 0
        ? groupedOrders.slice(
            page * rowsPerPage,
            page * rowsPerPage + rowsPerPage,
          )
        : groupedOrders

    return (
      <div className="container reports">
        <div className="report">
          <div className="topbar">
            <div>
              <p className="eyebrow">Admin area</p>
              <h1>Reports</h1>
              <div className="muted">
                Showing {groupedOrders.length} purchases
              </div>
            </div>

            <div className="topbar-actions">
              <Link className="back-link" to="/dashboard">
                <ArrowBackIcon /> Back to Admin
              </Link>

              <div className="download">
                <CSVLink data={this.handleDataCSV(groupedOrders)}>
                  Download <GetAppIcon />
                </CSVLink>
              </div>
            </div>
          </div>

          <Grid container spacing={2}>
            <Grid className="filters" item xs={12}>
              <TextField
                className="form-input"
                id="name"
                label="Name"
                type="text"
                value={name}
                onChange={(e) => this.handleFilterChange(e)}
              />

              <TextField
                className="form-input"
                id="email"
                label="Email"
                type="text"
                value={email}
                onChange={(e) => this.handleFilterChange(e)}
              />

              <TextField
                className="form-input"
                id="phone"
                label="Phone"
                type="text"
                value={phone}
                onChange={(e) => this.handleFilterChange(e)}
              />

              <TextField
                className="form-input"
                id="course"
                label="Product / Course"
                type="text"
                value={course}
                onChange={(e) => this.handleFilterChange(e)}
              />

              <TextField
                className="form-input"
                id="dateScheduled"
                label="Date Scheduled"
                type="text"
                value={dateScheduled}
                onChange={(e) => this.handleFilterChange(e)}
              />

              <TextField
                className="form-input"
                id="type"
                label="Type"
                type="text"
                value={type}
                onChange={(e) => this.handleFilterChange(e)}
              />

              <TextField
                className="form-input"
                id="quantity"
                label="Quantity"
                type="text"
                value={quantity}
                onChange={(e) => this.handleFilterChange(e)}
              />

              <TextField
                className="form-input"
                id="datePurchased"
                label="Date Purchased"
                type="text"
                value={datePurchased}
                onChange={(e) => this.handleFilterChange(e)}
              />

              <TextField
                className="form-input"
                id="voucher"
                label="Voucher"
                type="text"
                value={voucher}
                onChange={(e) => this.handleFilterChange(e)}
              />
            </Grid>

            <Grid item xs={12}>
              <div className="tableWrap">
                <TableContainer component={Paper} elevation={0}>
                  <Table className="table" aria-label="reports table">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Phone</TableCell>
                        <TableCell>Product Sold</TableCell>
                        <TableCell>Date Scheduled</TableCell>
                        <TableCell>Total Paid</TableCell>
                        <TableCell>Date Purchased</TableCell>
                        <TableCell>Voucher</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {orderRows.map((order) => (
                        <TableRow key={order.orderId}>
                          <TableCell component="th" scope="row">
                            {order?.user?.name}
                          </TableCell>

                          <TableCell>{order?.user?.email}</TableCell>

                          <TableCell>{order?.user?.phone}</TableCell>

                          <TableCell>
                            <strong>{this.getProductSold(order)}</strong>

                            {order.isComboOrder ? (
                              <div className="combo-label">Combo</div>
                            ) : (
                              ""
                            )}
                          </TableCell>

                          <TableCell>{order.scheduledDateTime}</TableCell>

                          <TableCell>€{order.totalPaid}</TableCell>

                          <TableCell>
                            {this.formatDate(order.date_purchase)}
                          </TableCell>

                          <TableCell>{order.voucher}</TableCell>
                        </TableRow>
                      ))}

                      {emptyRows > 0 && (
                        <TableRow style={{ height: 53 * emptyRows }}>
                          <TableCell colSpan={8} />
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  component="div"
                  rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
                  count={groupedOrders.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: { "aria-label": "rows per page" },
                    native: true,
                  }}
                  onChangePage={(event, newPage) =>
                    this.setState({ page: newPage })
                  }
                  onChangeRowsPerPage={(event) => {
                    this.setState({
                      rowsPerPage: parseInt(event.target.value, 10),
                      page: 0,
                    })
                  }}
                  ActionsComponent={TablePaginationActions}
                />
              </div>
            </Grid>
          </Grid>
        </div>
      </div>
    )
  }
}

export default Reports
